/**
 * Zone geofencing: whether it is on, which way the list reads, and what is on it.
 *
 * The polarity and the zone list are held apart from the checkbox so unticking and reticking does
 * not throw the selection away, and the preset is held apart from the list — which stays the
 * effective selection either way, so the guards here need no special case for a sender that
 * follows one.
 */

import * as React from 'react';
import { IndoorGeofencingMode, ProfileConfiguration, ZoneEntry } from '@ixicare/resources';
import { useZones } from '~hooks/zone';

export interface IndoorGeofencing {
  enabled: boolean;
  setEnabled: (next: boolean) => void;
  polarity: IndoorGeofencingMode;
  setPolarity: (next: IndoorGeofencingMode) => void;
  /** Asks before inverting, where there is a selection to lose. */
  handlePolarity: (next: IndoorGeofencingMode) => void;
  pendingPolarity?: IndoorGeofencingMode;
  setPendingPolarity: (next?: IndoorGeofencingMode) => void;
  detachOnBlock: (next: IndoorGeofencingMode) => void;
  zones: string[];
  setZones: (next: string[]) => void;
  inverted: string[];
  selectsEveryZone: boolean;
  presetId: string | null;
  setPresetId: (next: string | null) => void;
  allZones: ZoneEntry[];
  requiresOutdoorCover: boolean;
}

export const useIndoorGeofencing = (
  configuration: ProfileConfiguration | undefined,
  siteUuid?: string
): IndoorGeofencing => {
  // Absent polarity means Allow: every configuration written before the field existed is an
  // allow list.
  const [enabled, setEnabled] = React.useState(configuration?.['indoor-geofencing-enabled'] ?? false);
  const [polarity, setPolarity] = React.useState<IndoorGeofencingMode>(
    configuration?.['indoor-geofencing-mode'] ?? IndoorGeofencingMode.Allow
  );
  const [zones, setZones] = React.useState<string[]>(configuration?.['indoor-geofencing-zones'] ?? []);
  const [presetId, setPresetId] = React.useState<string | null>(configuration?.['indoor-geofencing-preset'] ?? null);
  const [pendingPolarity, setPendingPolarity] = React.useState<IndoorGeofencingMode>();

  // Hoisted out of the zone input: inverting a selection needs the complement, and the all-zones
  // guard needs the total. Also collapses what used to be two fetches into one.
  const allZones = useZones({ siteUuid });

  const inverted = React.useMemo(
    () => allZones.filter((z) => !zones.includes(z.uuid)).map((z) => z.uuid),
    [allZones, zones]
  );

  // A selection covering the whole site is not a restriction: it permits everywhere as an allow
  // list, and forbids everywhere as a block list. The API refuses both, so say so here.
  const selectsEveryZone = allZones.length > 0 && allZones.every((z) => zones.includes(z.uuid));

  // A preset only ever names allowed zones, so a sender reading their list the other way cannot be
  // following one - whether the zones are inverted or kept, they now mean the opposite of it.
  const detachOnBlock = (next: IndoorGeofencingMode) => {
    if (next === IndoorGeofencingMode.Block) setPresetId(null);
  };

  const handlePolarity = (next: IndoorGeofencingMode) => {
    if (next === polarity) return;
    // Nothing picked yet, so there is nothing to invert and nothing to lose.
    if (zones.length === 0) {
      setPolarity(next);
      return;
    }
    setPendingPolarity(next);
  };

  // An allow list says the wearer belongs in the zones named, so being anywhere else - including
  // outside the building - is a breach.
  const requiresOutdoorCover = enabled && polarity === IndoorGeofencingMode.Allow;

  return {
    enabled,
    setEnabled,
    polarity,
    setPolarity,
    handlePolarity,
    pendingPolarity,
    setPendingPolarity,
    detachOnBlock,
    zones,
    setZones,
    inverted,
    selectsEveryZone,
    presetId,
    setPresetId,
    allZones,
    requiresOutdoorCover,
  };
};
